import { forwardRef, useState } from "react";

import { cn, platformProps } from "../../lib";
import { Box } from "../../primitives";
import { TextAreaPrimitive } from "../../primitives/text-area";
import { getTextFieldVisualState, textFieldInputClass } from "../text-field";
import { textAreaContainerVariants, textAreaPlaceholderClass } from "./text-area.variants";
import type { TextAreaProps } from "./types";

/**
 * TextArea - Multi-line text input atom.
 *
 * Architecture mirrors TextField: Box container with border/bg variants,
 * TextArea primitive inside for the actual `<textarea>` (web) / `TextInput multiline` (native).
 *
 * @example
 * ```tsx
 * <TextArea placeholder="Enter description" />
 * <TextArea rows={6} state="error" />
 * <TextArea disabled defaultValue="Cannot edit" />
 * <TextArea resize placeholder="Drag to resize" />
 * ```
 */
export const TextArea = forwardRef<React.ElementRef<typeof TextAreaPrimitive>, TextAreaProps>(
  (
    {
      state = "default",
      disabled = false,
      rows = 4,
      resize = false,
      className,
      inputClassName,
      testID,
      id,
      onFocus,
      onBlur,
      ...textInputProps
    },
    ref,
  ) => {
    const [isFocused, setIsFocused] = useState(false);

    const visualState = getTextFieldVisualState({ disabled, isFocused, state });

    return (
      <Box
        {...platformProps({ slot: "text-area" })}
        className={cn(textAreaContainerVariants({ visualState }), className)}
      >
        <TextAreaPrimitive
          ref={ref}
          {...platformProps({ testID, id })}
          rows={rows}
          disabled={disabled}
          className={cn(
            "text-body-md md:text-body-md-dt native:font-inter-regular w-full flex-1 bg-transparent align-top outline-none",
            textFieldInputClass[visualState],
            textAreaPlaceholderClass[visualState],
            !resize && "web:resize-none",
            resize && "web:resize-y",
            inputClassName,
          )}
          onFocus={() => {
            setIsFocused(true);
            onFocus?.();
          }}
          onBlur={() => {
            setIsFocused(false);
            onBlur?.();
          }}
          {...textInputProps}
          invalid={state === "error"}
        />
      </Box>
    );
  },
);

TextArea.displayName = "TextArea";
